import React, { useEffect, useState } from 'react';
import { G, styles, api, fmtUsd } from '../theme.js';
import { Btn, Badge, Spinner, Select, Input } from '../ui.jsx';
import { Safe } from '../widgets/ErrorBoundary.jsx';

// CSM (Customer Success Manager / sales rep) performance portal.
// Two views:
//   • "My Numbers"  — what every CSM sees (their own conversion funnel)
//   • "Team"        — what owners see (scoreboard ranked by gross profit)
//
// Funnel: leads → dials → connects → booked → showed/no-show → revenue/profit.
// Profitability per dial / per booking / per show makes it easy for an owner
// to see whether the CSM's time is actually paying for itself.

const PRESETS = [
  { id:'7d',  label:'7 days',  days:7  },
  { id:'30d', label:'30 days', days:30 },
  { id:'90d', label:'90 days', days:90 },
  { id:'mtd', label:'Month to date' },
  { id:'ytd', label:'Year to date' },
];

export function CsmPortal({ user, toast }) {
  const isOwner = ['owner','admin','super_admin'].includes(user?.role);
  const [view, setView] = useState(isOwner ? 'team' : 'me');
  const [preset, setPreset] = useState('30d');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const range = (() => {
    if (from && to) return { from, to };
    const now = new Date();
    const iso = (d) => d.toISOString().slice(0,10);
    const p = PRESETS.find(x => x.id === preset);
    if (p?.days) return { from: iso(new Date(now.getTime() - p.days * 86400000)), to: iso(now) };
    if (preset === 'mtd') return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(now) };
    if (preset === 'ytd') return { from: iso(new Date(now.getFullYear(), 0, 1)), to: iso(now) };
    return { from: iso(new Date(now.getTime() - 30 * 86400000)), to: iso(now) };
  })();

  return <div style={{ padding:24 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:18, gap:14, flexWrap:'wrap' }}>
      <div>
        <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>CSM Performance</h1>
        <div style={{ color:G.textMuted, fontSize:13, marginTop:4 }}>
          Conversion funnel + profitability per dial / per booking / per show.
        </div>
      </div>
      <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
        {isOwner && <div style={{ display:'flex', gap:4, padding:4, background:G.input, borderRadius:8 }}>
          <Btn sz="sm" v={view==='me'?'primary':'ghost'} onClick={()=>setView('me')}>My Numbers</Btn>
          <Btn sz="sm" v={view==='team'?'primary':'ghost'} onClick={()=>setView('team')}>👥 Team</Btn>
        </div>}
        <Select value={preset} onChange={e=>{ setPreset(e.target.value); setFrom(''); setTo(''); }} style={{ maxWidth:180 }}>
          {PRESETS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
          <option value="custom">Custom range</option>
        </Select>
        {preset === 'custom' && <>
          <Input type="date" value={from} onChange={e=>setFrom(e.target.value)}/>
          <Input type="date" value={to} onChange={e=>setTo(e.target.value)}/>
        </>}
      </div>
    </div>

    {view === 'me'   && <Safe name="CsmSelfView"><SelfView range={range} toast={toast}/></Safe>}
    {view === 'team' && isOwner && <Safe name="CsmTeamView"><TeamView range={range} toast={toast}/></Safe>}
  </div>;
}

function SelfView({ range, toast }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api(`/api/csm-metrics/me?from=${range.from}&to=${range.to}`).then(setData).catch(e=>toast?.(e.message,'error'));
  }, [range.from, range.to]);
  if (!data) return <Spinner sz={32}/>;
  return <FunnelView data={data}/>;
}

function TeamView({ range, toast }) {
  const [data, setData] = useState(null);
  const [openUserId, setOpenUserId] = useState(null);
  const [openMetrics, setOpenMetrics] = useState(null);
  useEffect(() => {
    setData(null);
    api(`/api/csm-metrics/team?from=${range.from}&to=${range.to}`).then(setData).catch(e=>toast?.(e.message,'error'));
  }, [range.from, range.to]);
  useEffect(() => {
    if (!openUserId) return;
    setOpenMetrics(null);
    api(`/api/csm-metrics/user/${openUserId}?from=${range.from}&to=${range.to}`).then(setOpenMetrics).catch(()=>{});
  }, [openUserId, range.from, range.to]);
  if (!data) return <Spinner sz={32}/>;
  return <div>
    <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:18 }}>
      <thead><tr>
        {['CSM','Role','Leads','Dials','Connects','Booked','Shows','No-Shows','Revenue','Gross Profit','$ / Booking',''].map(h => (
          <th key={h} style={styles.th}>{h}</th>
        ))}
      </tr></thead>
      <tbody>
        {data.team.map((row, i) => {
          const m = row.metrics;
          return <tr key={row.user.id} style={{ cursor:'pointer' }} onClick={()=>setOpenUserId(row.user.id)}>
            <td style={{ ...styles.td, fontWeight:700 }}>
              {i===0 && <span style={{ marginRight:6 }}>🏆</span>}{row.user.name}
            </td>
            <td style={styles.td}><Badge c={G.purple} bg={G.purpleDim}>{row.user.role}</Badge></td>
            <td style={styles.td}>{m.funnel.leads}</td>
            <td style={styles.td}>{m.funnel.dials}</td>
            <td style={styles.td}>{m.funnel.connects} <small style={{ color:G.textMuted }}>({m.rates.connectRate}%)</small></td>
            <td style={styles.td}>{m.funnel.booked} <small style={{ color:G.textMuted }}>({m.rates.bookingRate}%)</small></td>
            <td style={styles.td}><span style={{ color:G.green, fontWeight:700 }}>{m.funnel.shows}</span></td>
            <td style={styles.td}><span style={{ color:G.red, fontWeight:700 }}>{m.funnel.noShows}</span></td>
            <td style={styles.td}>{fmtUsd(m.money.revenue)}</td>
            <td style={styles.td}><strong>{fmtUsd(m.money.grossProfit)}</strong></td>
            <td style={styles.td}>{fmtUsd(m.money.revenuePerBooking)}</td>
            <td style={styles.td}><Btn sz="xs" v="ghost">View →</Btn></td>
          </tr>;
        })}
      </tbody>
    </table>
    {openUserId && <div style={{ ...styles.card, padding:18, marginTop:14, borderColor:G.purple+'66' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
        <h2 style={{ margin:0, fontSize:18 }}>
          {data.team.find(t => t.user.id === openUserId)?.user.name || 'CSM'} — detail
        </h2>
        <Btn sz="sm" v="ghost" onClick={()=>setOpenUserId(null)}>✕ Close</Btn>
      </div>
      {!openMetrics ? <Spinner sz={20}/> : <FunnelView data={openMetrics}/>}
    </div>}
  </div>;
}

function FunnelView({ data }) {
  const f = data.funnel;
  const m = data.money;
  const r = data.rates;
  const StatTile = ({ label, value, sub, color }) => (
    <div style={{ ...styles.card, padding:14, borderColor: (color || G.cardBorder)+'66' }}>
      <div style={{ fontSize:11, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</div>
      <div style={{ fontSize:24, fontWeight:800, marginTop:4, color: color || G.text }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>{sub}</div>}
    </div>
  );
  return <div>
    {/* Funnel row */}
    <div style={{ display:'grid', gridTemplateColumns:'repeat(8, 1fr)', gap:10, marginBottom:18 }}>
      <StatTile label="Leads"      value={f.leads}/>
      <StatTile label="Dials"      value={f.dials}/>
      <StatTile label="Connects"   value={f.connects} sub={`${r.connectRate}% of dials`} color={G.cyan}/>
      <StatTile label="Booked"     value={f.booked}   sub={`${r.bookingRate}% of connects`} color={G.purple}/>
      <StatTile label="Showed"     value={f.shows}    sub={`${r.showRate}% show rate`} color={G.green}/>
      <StatTile label="No-shows"   value={f.noShows}  color={G.red}/>
      <StatTile label="Cancelled"  value={f.cancellations} color={G.yellow}/>
      <StatTile label="Pending"    value={f.pendingShow}/>
    </div>

    {/* Money row */}
    <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:10, marginBottom:18 }}>
      <StatTile label="Revenue (paid)"  value={fmtUsd(m.revenue)}   sub={`${m.paidInvoiceCount} paid invoices`} color={G.green}/>
      <StatTile label="Gross Profit"    value={fmtUsd(m.grossProfit)} sub={`${m.cogsPct}% COGS assumed`} color={G.green}/>
      <StatTile label="$ / Booking"     value={fmtUsd(m.revenuePerBooking)} sub={`Revenue per appt`}/>
      <StatTile label="$ / Show"        value={fmtUsd(m.profitPerShow)} sub={`Gross profit per show`} color={G.purple}/>
    </div>

    {/* Per-unit profitability — the question owners actually want answered */}
    <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:10 }}>
      <StatTile label="Profit per dial"     value={fmtUsd(m.profitPerDial)}     sub="Is your time worth answering the phone?"/>
      <StatTile label="Profit per booking"  value={fmtUsd(m.profitPerBooking)}  sub="Worth investing in better bookings?"/>
      <StatTile label="Profit per show"     value={fmtUsd(m.profitPerShow)}     sub="The number that pays your salary"/>
    </div>

    <div style={{ marginTop:18, fontSize:11, color:G.textMuted, padding:12, background:G.input, borderRadius:8 }}>
      <strong>How this is calculated:</strong> Leads = contacts assigned to this CSM in the window. Dials = outbound calls placed by this user. Connects = calls with duration &gt; 30s or explicitly marked connected. Booked = jobs where csm_user_id matches. Show / no-show comes from the job's show_status. Revenue = paid invoices linked to showed jobs. Gross profit = revenue × (1 − account COGS %). Override per-job actuals from the invoice / pricebook to get exact profit numbers.
    </div>
  </div>;
}
